/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : ConfigureWorkFlowStore.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Redux Slice for Configure WorkFlow state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Sep 14, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Sep 14, 2020
 * Description   : Add code to maintain state for Configure Workflow data
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'stores/GlobalStore';
import {
  IConfigureWorkFlowState,
  IConfigureScreenData,
  EnumConfigureFlow,
  IConfigureScreenParams,
  IDownloadConfigFile,
  ISaveConfigResponse,
  ISaveFinalConfigResponse,
  ISaveFinalConfigRequestParam,
  ISaveConfigData,
  ISaveConfigRequestParam,
} from 'models/configuration';
import { apiCallBegan } from 'stores/middlewares';
import { IErrorState } from 'models/ui';

const initialState: IConfigureWorkFlowState = {
  configureFlow: EnumConfigureFlow.New,
  configureScreenParams: {
    salesOrder: '',
    configId: '',
    soldTo: '',
    productNumber : ''
  },
  configureScreenData: {
    make: '',
    model: '',
    configId: null,
    stc: '',
    installMode: '',
    antenna: '',
    quantity: 0,
    aircraftDetails: [],
    softwareFeatures: [],
    productNumber : '',
    productDescription : '',
    remainingQuantity : 0,
    totalQuantity : 0,
    optRemainingQuantity : 0,
    optTotalQuantity : 0
  },
  downloadConfigLoader: false,
  configAfterSaveDetails: {
    loader: false,
    details: null,
    pythonError: false,
  },
  saveFinalConfig: {
    loader: false,
    response: null,
    error: false,
  },
};

const configureWorkFlowSlice = createSlice({
  name: 'configureWorkFlow',
  initialState,
  reducers: {
    setConfigureScreenData(
      configureWorkFlowState: IConfigureWorkFlowState,
      action: PayloadAction<IConfigureScreenData>
    ) {
      configureWorkFlowState.configureScreenData = action.payload;
    },
    resetConfigureFlowState(configureWorkFlowState: IConfigureWorkFlowState, action: PayloadAction<EnumConfigureFlow>) {
      configureWorkFlowState.configureFlow = action.payload;
      configureWorkFlowState.configureScreenParams = initialState.configureScreenParams;
      configureWorkFlowState.configureScreenData = initialState.configureScreenData;
    },
    resetConfigureScreenData(configureWorkFlowState: IConfigureWorkFlowState) {
      configureWorkFlowState.configureScreenData = initialState.configureScreenData;
    },
    setConfigureScreenParams(
      configureWorkFlowState: IConfigureWorkFlowState,
      action: PayloadAction<IConfigureScreenParams>
    ) {
      configureWorkFlowState.configureScreenParams = action.payload;
    },
    beginDownloadConfig(configureWorkFlowState: IConfigureWorkFlowState) {
      configureWorkFlowState.downloadConfigLoader = true;
    },
    stopDownloadConfig(configureWorkFlowState: IConfigureWorkFlowState) {
      configureWorkFlowState.downloadConfigLoader = false;
    },
    beginSaveConfig(configureWorkFlowState: IConfigureWorkFlowState) {
      configureWorkFlowState.configAfterSaveDetails.loader = true;
      configureWorkFlowState.configAfterSaveDetails.pythonError = false;
    },
    stopSaveConfig(configureWorkFlowState: IConfigureWorkFlowState, action: PayloadAction<IErrorState>) {
      configureWorkFlowState.configAfterSaveDetails.loader = false;
      if (action.payload.errorMessage === 'Python validation failed') {
        configureWorkFlowState.configAfterSaveDetails.pythonError = true;
      }
    },
    setConfigAfterSaveDetails(
      configureWorkFlowState: IConfigureWorkFlowState,
      action: PayloadAction<ISaveConfigResponse>
    ) {
      configureWorkFlowState.configAfterSaveDetails.details = action.payload;
      configureWorkFlowState.configAfterSaveDetails.loader = false;
    },
    beginSaveFinalConfig(configureWorkFlowState: IConfigureWorkFlowState) {
      configureWorkFlowState.saveFinalConfig.loader = true;
    },
    setSaveFinalConfigError(configureWorkFlowState: IConfigureWorkFlowState) {
      configureWorkFlowState.saveFinalConfig.loader = false;
      configureWorkFlowState.saveFinalConfig.error = true;
    },
    setSaveFinalConfig(
      configureWorkFlowState: IConfigureWorkFlowState,
      action: PayloadAction<ISaveFinalConfigResponse>
    ) {
      configureWorkFlowState.saveFinalConfig.response = action.payload;
      configureWorkFlowState.saveFinalConfig.loader = false;
    },
  },
  extraReducers: builder =>
    builder.addCase('ui/setConfigureCurrentStep', (configureWorkFlowState: IConfigureWorkFlowState) => {
      configureWorkFlowState.saveFinalConfig.error = false;
    }),
});

export const {
  setConfigureScreenData,
  resetConfigureFlowState,
  setConfigureScreenParams,
  beginDownloadConfig,
  stopDownloadConfig,
  beginSaveConfig,
  stopSaveConfig,
  setConfigAfterSaveDetails,
  beginSaveFinalConfig,
  setSaveFinalConfig,
  setSaveFinalConfigError,
  resetConfigureScreenData,
} = configureWorkFlowSlice.actions;

export const configureWorkFlowReducer = configureWorkFlowSlice.reducer;

export const fetchConfigureScreenData = (): AppThunk => async (dispatch, getState) => {
  const currentState = getState();
  const currentConfigureFlow = currentState.configureWorkFlow.configureFlow;

  let soldTo: string = '';
  let salesOrder: string = '';

  if (currentConfigureFlow === EnumConfigureFlow.New) {
    soldTo = currentState.customer.selectedCustomer!.soldTo;
    salesOrder = currentState.order.selectedOrder!.salesOrder;
  } else if (currentConfigureFlow === EnumConfigureFlow.Edit) {
    soldTo = currentState.configureWorkFlow.configureScreenParams.soldTo;
    salesOrder = currentState.configureWorkFlow.configureScreenParams.salesOrder;
  }

  const { make, model, stc, installMode, configurations } = currentState.confirmStc.selectedConfirmStc!;

  const url = `/configure-screen-data/${salesOrder}/${soldTo}`;
  const params = {
    make,
    model,
    stc,
    'install-mode': installMode,
    'productNumber': configurations
  };

  dispatch(
    apiCallBegan({
      url,
      params,
      onSuccess: setConfigureScreenData.type,
      showGlobalError: true,
    })
  );
};

export const fetchAddConfigureScreenData = (
  soldTo: string,
  configId: string,
  salesOrderNumber: string
): AppThunk => async dispatch => {
  const url = `/configure-screen-data/${salesOrderNumber}`;
  const params = {
    'config-id': configId,
    'sold-to': soldTo,
  };

  dispatch(
    apiCallBegan({
      url,
      params,
      onSuccess: setConfigureScreenData.type,
      showGlobalError: true,
    })
  );
};

export const fetchDownloadConfigFile = (
  inputParams: IDownloadConfigFile,
  salesOrderNo: string
): AppThunk => async dispatch => {
  const url = `/configuration/download/${salesOrderNo}`;
  const params = { 'sold-to': inputParams.soldTo, 'config-id': inputParams.configId };

  dispatch(
    apiCallBegan({
      url,
      params,
      responseType: 'blob',
      onStart: beginDownloadConfig.type,
      onSuccess: stopDownloadConfig.type,
      showGlobalError: true,
      onError: stopDownloadConfig.type,
    })
  );
};

export const saveConfiguration = (configFormData: ISaveConfigData, action: string): AppThunk => async (
  dispatch,
  getState
) => {
  const { sub } = getState().auth.userInfo!;
  const { customerName, soldTo, address } = getState().customer.selectedCustomer!;
  const url = `/configure-screen-data?action=${action}`;
  const data: ISaveConfigRequestParam = {
    ...configFormData,
    userDetails: {
      userName: sub,
      accountName: customerName,
      honeywellAccountCode: soldTo,
      addressLine1: address,
    },
  };

  dispatch(
    apiCallBegan({
      url,
      method: 'PATCH',
      data,
      onStart: beginSaveConfig.type,
      onSuccess: setConfigAfterSaveDetails.type,
      showGlobalError: true,
      onError: stopSaveConfig.type,
    })
  );
};

export const saveFinalConfiguration = ({ fileName }: ISaveFinalConfigRequestParam): AppThunk => async (
  dispatch,
  getState
) => {
  const { salesOrder, soldTo, configId, productNumber } = getState().configureWorkFlow.configureScreenParams;
  const { sub } = getState().auth.userInfo!;
  const { customerName, address } = getState().customer.selectedCustomer!;

  const url = `/configuration/${salesOrder}`;
  const params = {
    'sold-to': soldTo,
    'config-id': configId,
    'productNumber': productNumber
  };

  const data = {
    fileName,
    userDetails: {
      userName: sub,
      accountName: customerName,
      honeywellAccountCode: soldTo,
      addressLine1: address,
    },
  };

  dispatch(
    apiCallBegan({
      url,
      method: 'POST',
      params,
      data,
      onStart: beginSaveFinalConfig.type,
      onSuccess: setSaveFinalConfig.type,
      onError: setSaveFinalConfigError.type,
    })
  );
};

export const selectConfigureFlow = (state: RootState) => state.configureWorkFlow.configureFlow;
export const selectConfigureParamData = (state: RootState) => state.configureWorkFlow.configureScreenParams;
export const selectConfigureScreenData = (state: RootState) => state.configureWorkFlow.configureScreenData;
export const selectDownloadConfigLoader = (state: RootState) => state.configureWorkFlow.downloadConfigLoader;
export const selectSaveConfigurationDetails = (state: RootState) =>
  state.configureWorkFlow.configAfterSaveDetails.details;
export const selectPythonError = (state: RootState) => state.configureWorkFlow.configAfterSaveDetails.pythonError;
export const selectSaveConfigLoader = (state: RootState) => state.configureWorkFlow.configAfterSaveDetails.loader;
export const selectSaveFinalConfigLoader = (state: RootState) => state.configureWorkFlow.saveFinalConfig.loader;
export const selectSaveFinalConfigError = (state: RootState) => state.configureWorkFlow.saveFinalConfig.error;
